import { css } from '@emotion/css'
import { LoadingOverlay } from '@mantine/core'
import type { VC } from '@semios/app-platform-value-type-definitions'
import type { TNavigationKey } from 'App/Map/LayerNav/Layers/generateNavigationMenu/generateFlatNavigationList'
import { OverviewGridTable } from 'components/GridTable/OverviewGridTable'
import { translate } from 'i18n/i18n'
import { isEmpty } from 'lodash'
import moment from 'moment-timezone'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import type { TValueGroup } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { type selectedValueGroupsStore } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { camelize } from 'utils/camelize'
import { useApiCurrentValues } from '../_utils/useApiCurrentValues'
import { valueGroupEnums } from './valueGroupEnums'

export const PropertySectionTables = ({
  refreshKey,
  containerHeight,
  containerWidth,
  preventFetch,
  selectedValueGroups,
  selectedValueGroupsArray,
  layersActive,
}: {
  refreshKey?: number
  containerHeight: number
  containerWidth: number
  preventFetch: boolean
  selectedValueGroups: ReturnType<typeof selectedValueGroupsStore.getState>['selectedValueGroups']
  selectedValueGroupsArray: { label: string; value: TNavigationKey; checked: boolean }[]
  layersActive: string[]
}) => {
  const selectedFieldAssets = selectedFieldAssetsStore.useSelector((s) => s)
  const timestamp = moment.tz().format()
  const insects = fieldAssetStore.useSelector((s) => s.insects ?? {})
  const { permissions } = userDetailsStore.getState()
  const insectIds: number[] = []

  if (layersActive.includes('Pests')) {
    selectedValueGroupsArray.forEach((valueGroup) => {
      Object.values(insects).forEach((insect) => {
        const dbName = translate.phrases.dbInsectName(
          insect.name as keyof typeof translate.phrases.dbInsectName,
        )

        if (valueGroup.label === dbName) {
          insectIds.push(insect.insectId)
        }
      })
    })
  }

  let valuesToRequestPoints: Partial<Record<keyof VC.TValuesAvailableForPoints, boolean>> = {}
  let valuesToRequestBlocks: Partial<Record<keyof VC.TValuesAvailableForBlocks, boolean>> = {}
  let valuesToRequestProperties: Partial<Record<keyof VC.TValuesAvailableForProperties, boolean>> = {}

  layersActive.forEach((layer) => {
    const key = layer
      .toLowerCase()
      .replace(/(?:^\w|\s+(\w))/g, (match, p1) => (p1 ? p1.toUpperCase() : match)) as keyof ReturnType<
      typeof valueGroupEnums
    >

    const trapInsectIds = insectIds
      ? insectIds.filter((id) => permissions[`VIEW_PESTS_TRAP_CATCHES_ID_${id}`])
      : []

    const value = valueGroupEnums(insectIds, trapInsectIds)[key]
    const points = value && 'points' in value ? value.points : undefined
    const blocks = value && 'blocks' in value ? value.blocks : undefined
    const properties = value && 'properties' in value ? value.properties : undefined

    Object.keys(selectedValueGroups as TValueGroup[]).forEach((valueGroup: string) => {
      let groupKey = valueGroup as TValueGroup

      if (selectedValueGroups[groupKey] !== false) {
        if (valueGroup === 'air_temperature') {
          groupKey = 'temperature' as TValueGroup
        } else if (valueGroup === 'sprays') {
          groupKey = 'spray_conditions' as TValueGroup
        } else if (valueGroup.startsWith('irrigation_activity')) {
          groupKey = 'irrigation_activity' as TValueGroup
        }

        if (groupKey === 'spray_conditions' && points) {
          points &&
            Object.keys(points).forEach((key) => {
              valuesToRequestPoints = { ...valuesToRequestPoints, ...points[key] }
            })
        }

        if (groupKey.startsWith('degree_days')) {
          blocks &&
            Object.keys(blocks).forEach((block) => {
              valuesToRequestBlocks = { ...valuesToRequestBlocks, ...blocks[block] }
            })
        }

        if (groupKey.startsWith('trap_catches')) {
          properties &&
            Object.keys(properties).forEach((property) => {
              valuesToRequestProperties = { ...valuesToRequestProperties, ...properties[property] }
            })
        }

        if (points) {
          valuesToRequestPoints = {
            ...valuesToRequestPoints,
            ...points[camelize(groupKey)],
          }
        }

        if (blocks) {
          valuesToRequestBlocks = { ...valuesToRequestBlocks, ...blocks[camelize(groupKey)] }
        }
      }
    })
  })

  const { properties } = fieldAssetStore.useSelector((s) => ({
    properties: s?.properties ?? [],
  }))

  const allPropertyIds = Object.keys(properties)

  const { data, loading: isLoading } = useApiCurrentValues({
    args: {
      ...(Object.values(valuesToRequestBlocks).length > 0 && {
        blocks: {
          propertyIds: allPropertyIds.map(Number),
          values: valuesToRequestBlocks,
        },
      }),
      ...(Object.values(valuesToRequestPoints).length > 0 && {
        points: {
          propertyIds: allPropertyIds.map(Number),
          values: valuesToRequestPoints,
        },
      }),
      ...(Object.values(valuesToRequestProperties).length > 0 && {
        properties: {
          propertyIds: allPropertyIds.map(Number),
          values: valuesToRequestProperties,
        },
      }),
    },
    preventFetch:
      preventFetch ||
      (isEmpty(valuesToRequestBlocks) &&
        isEmpty(valuesToRequestPoints) &&
        isEmpty(valuesToRequestProperties)),
    watchers: [refreshKey ?? 0],
  })

  return (
    <div
      className={css({
        width: containerWidth,
        height: containerHeight,
        maxHeight: containerHeight,
        overflow: 'auto',
        touchAction: 'pan-x pan-y', //disable pinch to zoom to avoid mobile view bug with the map
      })}
    >
      <LoadingOverlay visible={isLoading} style={{ pointerEvents: 'none' }} />
      <OverviewGridTable
        containerWidth={containerWidth}
        key={selectedFieldAssets.property}
        data={data}
        timestamp={timestamp}
        isLoading={isLoading}
      />
    </div>
  )
}
